import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Plus, Trash2, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getDeviceId } from "@/utils/deviceId";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getModeIcon = (mode) => {
  switch (mode) {
    case "chat":
      return "💬";
    case "resume":
      return "📄";
    case "website":
      return "🌐";
    case "budget":
      return "💰";
    default:
      return "💬";
  }
};

const getModeLabel = (mode) => {
  switch (mode) {
    case "chat":
      return "Chat";
    case "resume":
      return "Resume";
    case "website":
      return "Website";
    case "budget":
      return "Budget";
    default:
      return "Chat";
  }
};

const ConversationSidebar = ({
  isOpen,
  onToggle,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  currentMode,
}) => {
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchConversations = useCallback(async () => {
    setIsLoading(true);
    try {
      // Include user's device ID to only fetch their conversations
      const deviceId = getDeviceId();
      const response = await axios.get(`${API}/conversations`, {
        params: { user_id: deviceId }
      });
      setConversations(response.data || []);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations, currentConversationId]);

  const handleDeleteConversation = async (e, conversationId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/conversations/${conversationId}`);
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      if (currentConversationId === conversationId) {
        onNewConversation();
      }
      toast.success("Conversation deleted");
    } catch (error) {
      console.error("Error deleting conversation:", error);
      toast.error("Failed to delete conversation");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  return (
    <>
      {/* Toggle Button - Always visible */}
      <motion.button
        onClick={onToggle}
        className={`fixed top-20 z-40 p-2 bg-gradient-to-r from-primary/80 to-secondary/80 rounded-r-lg shadow-lg shadow-primary/30 transition-all ${
          isOpen ? "left-72" : "left-0"
        }`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isOpen ? (
          <ChevronLeft size={20} className="text-white" />
        ) : (
          <ChevronRight size={20} className="text-white" />
        )}
      </motion.button>

      {/* Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: -288, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -288, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 h-full w-72 bg-card/95 backdrop-blur-xl border-r border-white/10 z-30 flex flex-col"
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-mono text-lg font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                  History
                </h2>
              </div>

              {/* New Chat Button */}
              <motion.button
                onClick={onNewConversation}
                className="w-full flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-primary/20 to-secondary/20 border border-primary/30 rounded-lg hover:from-primary/30 hover:to-secondary/30 transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Plus size={18} className="text-primary" />
                <span className="font-mono text-sm text-white/90">New Conversation</span>
              </motion.button>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 text-white/20" />
                  <p className="text-sm text-white/50 font-mono">No conversations yet</p>
                  <p className="text-xs text-white/30 mt-1">Start chatting to create one!</p>
                </div>
              ) : (
                conversations.map((conversation) => (
                  <motion.div
                    key={conversation.id}
                    onClick={() => onSelectConversation(conversation.id, conversation.mode)}
                    className={`group relative p-3 rounded-lg cursor-pointer transition-all ${
                      currentConversationId === conversation.id
                        ? "bg-gradient-to-r from-primary/30 to-secondary/30 border border-primary/50"
                        : "hover:bg-white/5 border border-transparent hover:border-white/10"
                    }`}
                    whileHover={{ x: 4 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-lg">{getModeIcon(conversation.mode)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white/90 truncate">
                          {conversation.title || "New Conversation"}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/60 font-mono">
                            {getModeLabel(conversation.mode)}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-white/40">
                            <Clock size={10} />
                            {formatDate(conversation.updated_at || conversation.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Delete Button */}
                    <motion.button
                      onClick={(e) => handleDeleteConversation(e, conversation.id)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg bg-destructive/20 opacity-0 group-hover:opacity-100 hover:bg-destructive/40 transition-all"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Trash2 size={14} className="text-destructive" />
                    </motion.button>
                  </motion.div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10">
              <p className="text-xs text-white/30 font-mono text-center">
                {conversations.length} conversation{conversations.length !== 1 ? "s" : ""}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay for mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onToggle}
            className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ConversationSidebar;
